import Cookie from 'js-cookie';
import api from './http';
import { ContactType, QueryLegalPersonRes, SendOtpRes, Session, VerifyOtpRes } from './types';

/**
 * If there are any verified development for the current session, returns the
 * the link to the verified development customer portal along data for other
 * developments in which the legal person of the session owns a contract.
 * If there are no verified developments in the session, returns 204 (No Content)
 */
export async function getSession(): Promise<Session | null> {
  if (!Cookie.get('xcsrftoken')) return null;
  const { data, status } = await api.get('/api/v1/current-session');
  if (status === 204) return null;
  return data;
}

/**
 * Clear the session cookies and mark the session as deleted in the database
 * The session cookies deleted are "sid" and "xcsrftoken"
 */
export async function deleteSession(): Promise<void> {
  await api.delete('/api/v1/current-session');
}

/**
 * When there is a legal persons with the given document in the database,
 * returns it along with the developments and contacts associated with it
 * and creates a new session for it. Two session cookies are created: one
 * http only session cookie called "sid" which stores the session id and
 * another non http only csrf token cookie called "xcsrftoken" which stores
 * the csrf token that should be sent in every subsequent request in the
 * X-Csrf-Token header
 *
 * When there are no matched customers, returns 204. Does not set any cookies.
 * If an ip address makes too many hourly requests to this function, returns 429.
 * This method is also rate limited per document to avoid brute force attacks.
 */
export async function queryLegalPerson(document: string): Promise<QueryLegalPersonRes> {
  const { data } = await api.get('/api/v1/legal-persons', { document });
  return data;
}

export async function sendOtp(
  document: string,
  developmentId: number,
  contactType: ContactType,
  contactId: number,
): Promise<SendOtpRes> {
  const { data } = await api.post(`/api/v1/send-otp`, { document, developmentId, contactType, contactId });
  return data;
}

export async function resendOtp(otpId: string) {
  await api.post(`/api/v1/resend-otp`, { otpId });
}

/**
 * Each otp is valid for 5 minutes and 3 attempts.
 *
 * If rememberMe is true, save the development as verified in the session. The
 * rememberMe is per development, so a user may choose to remember one development
 * and not remember another. We don't clear the session if rememberMe is false,
 * to do so the user must click in the logout button
 *
 * @throws VerifyOtpError
 */
export async function verifyOtp(otpId: string, token: string, rememberMe: boolean): Promise<VerifyOtpRes> {
  const { data } = await api.post(`/api/v1/verify-otp`, { otpId, token, rememberMe });
  return data;
}
